import { EventEmitter, inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ICfTurnstileConfiguration } from './interfaces/cf-turnstile-configuration';

const SCRIPT_ID = 'ng-cf-turnstile';
const CALLBACK_NAME = 'onloadTurnstileCallback';

export const CF_TURNSTILE_SITE_KEY = new InjectionToken<string | null>('CF_TURNSTILE_SITE_KEY');

@Injectable({
  providedIn: 'root',
})
export class CfTurnstileService {
  private zone = inject(NgZone);
  private doc = inject(DOCUMENT);

  readonly resolved = new EventEmitter<string | null>();
  readonly errored = new EventEmitter<string | null>();

  private version = 0;
  private widgetId: string;

  public createWidget(el: HTMLElement, options: ICfTurnstileConfiguration): void {
    if (!options?.sitekey) {
      return;
    }

    const turnstileOptions: ICfTurnstileConfiguration = {
      ...{
        callback: (token: string) => {
          this.zone.run(() => this.resolved.emit(token));
        },
        // eslint-disable-next-line
        // @ts-ignore
        'error-callback': (errorCode: string): boolean => {
          this.zone.run(() => this.errored.emit(errorCode));
          // Returning false causes Turnstile to log error code as a console warning.
          return false;
        },
        'expired-callback': () => {
          this.zone.run(() => this.reset());
        },
      },
      ...options,
    };

    window[CALLBACK_NAME] = () => {
      if (!el) {
        return;
      }

      this.widgetId = window.turnstile.render(el, turnstileOptions);
    };

    if (this.scriptLoaded()) {
      window[CALLBACK_NAME]();
      return;
    }

    const script = this.doc.createElement('script');
    script.src = `${this.getCloudflareTurnstileUrl()}?render=explicit&onload=${CALLBACK_NAME}`;
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    this.doc.head.appendChild(script);
  }

  public reset(): void {
    if (this.widgetId) {
      this.resolved.emit(null);
      window.turnstile.reset(this.widgetId);
    }
  }

  public remove(): void {
    if (this.widgetId) {
      window.turnstile.remove(this.widgetId);
    }
  }

  public execute(): void {
    if (this.widgetId) {
      window.turnstile.execute(this.widgetId);
    }
  }

  public scriptLoaded(): boolean {
    return !!this.doc.getElementById(SCRIPT_ID);
  }

  private getCloudflareTurnstileUrl(): string {
    if (this.version === 0) {
      return 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    }

    throw 'Version not defined in cf-turnstile component.';
  }
}
